import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const FREE_SHIP = 65;

export function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, lineKey, count } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          size: i.size,
          variant: i.variant || null,
          quantity: i.quantity,
        })),
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  const progress = Math.min(100, (subtotal / FREE_SHIP) * 100);
  const remaining = Math.max(0, FREE_SHIP - subtotal);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-moss-dark/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-cream"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E4DDD0]">
              <h2 className="font-serif text-2xl text-moss">Your Ritual ({count})</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="p-1 text-moss hover:text-gold">
                <X className="h-5 w-5" />
              </button>
            </div>

            {items.length > 0 && (
              <div className="px-6 py-4 border-b border-[#E4DDD0]">
                <div className="flex justify-between text-xs font-mono text-muted mb-2">
                  <span>{remaining > 0 ? `$${remaining.toFixed(2)} to free shipping` : "You've unlocked free shipping ✦"}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-cream-3 overflow-hidden">
                  <div className="h-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <p className="font-serif text-2xl text-moss mb-2">Your jar is empty</p>
                  <p className="text-muted text-sm">Add a butter your skin can actually read.</p>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((i) => {
                    const key = lineKey(i);
                    return (
                      <li key={key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                        <img src={i.image} alt={i.name} className="h-24 w-20 rounded-sm object-cover bg-cream-3" />
                        <div className="flex-1">
                          <p className="font-serif text-lg leading-tight text-moss">{i.name}</p>
                          <p className="text-xs text-muted font-mono mt-1">{i.size}{i.variant ? ` · ${i.variant}` : ""}</p>
                          <p className="text-sm text-gold font-medium mt-1">${i.price.toFixed(2)}</p>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex items-center border border-[#E4DDD0] rounded-sm">
                              <button onClick={() => updateQty(key, i.quantity - 1)} className="px-2 py-1 text-moss hover:text-gold" data-testid={`cart-dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                              <span className="px-2 text-sm w-6 text-center">{i.quantity}</span>
                              <button onClick={() => updateQty(key, i.quantity + 1)} className="px-2 py-1 text-moss hover:text-gold" data-testid={`cart-inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                            </div>
                            <button onClick={() => removeItem(key)} className="text-muted hover:text-terracotta" data-testid={`cart-remove-${i.product_id}`}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#E4DDD0] px-6 py-5">
                <div className="flex justify-between mb-1">
                  <span className="text-muted">Subtotal</span>
                  <span className="font-serif text-xl text-moss" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted mb-4">Shipping & taxes calculated at checkout.</p>
                <button
                  onClick={checkout}
                  disabled={loading}
                  className="btn-gold w-full py-3.5 rounded-sm font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="cart-checkout-button"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : "Secure Checkout"}
                </button>
                <p className="mt-3 text-center text-[11px] font-mono text-muted">Card · Apple Pay · Google Pay via Stripe</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
